<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import moment from "moment";

import { useServiceQueueStore } from "@/store/serviceQueue";
import {
  normalizeRuntimeResponse,
  autoSelectSingleItemFields,
  summarizeArrayChoices,
} from "@/shared/serviceResponse";
import { toast } from "@/shared/store/alertStore";
import { useConfirm } from "@/shared/ui/alert-dialog";
import TipTapEditor2 from "@/features/document-editor/ui/TipTapEditor2.vue";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";

import AddParticipants from "@/pages/admin/home/notarial_actions/components/AddParticipantsToNotarialAction.vue";
import AddParticipantHeader from "@/pages/admin/home/notarial_actions/components/AddParticipantHeader.vue";
import ParticipantList from "@/pages/admin/home/notarial_actions/components/ParticipantList.vue";

import ServiceExecutionBlock from "@/pages/admin/home/notarial_actions/components/ServiceExecutionBlock.vue";
import { StepRunList } from "@/entities/transaction";
import NotarialActionDataView from "@/pages/admin/home/notarial_actions/tabs/view/NotarialActionDataView.vue";
import PreviewTipTap2 from "@/features/document-editor/ui/PreviewTipTap2.vue";
import { ParticipantDropdown } from "@/features/participant-dropdown";
import { convertNotaryToFields } from "@/shared/lib/notaryToFields.v2";
import { dateToWords } from "@/shared/lib/dateToWords";
import { filterStepsForParticipant } from "@/shared/lib/queueStepFilter";
import {
  updateMentionsInAllLanguages,
  fillEmptyMentionsWithDashInAllLanguages,
} from "@/shared/mention";
import {
  mapRepeatSectionInAllLanguages,
  removeRepeatSectionByCodeInAllLanguages,
  restoreOriginalRepeatSection,
} from "@/shared/mention/placeholderReplacer";
import PlaceholderMappingDialog from "@/pages/admin/home/notarial_actions/components/PlaceholderMappingDialog.vue";
import { getSignatureList, useParticipants } from "@/entities/transaction";
import { useParticipantTypes } from "@/entities/directory";
import { StatusCode } from "@/shared/config/statusDictionary";

const props = defineProps({
  form: {
    type: Object,
    default: () => ({}),
  },
  template: {
    type: Object,
    default: () => ({}),
  },
  templateItems: {
    type: Array,
    default: () => [],
  },
});

const route = useRoute();
const { t, locale } = useI18n();
const serviceQueue = useServiceQueueStore();
const confirm = useConfirm();

// computed
const uuid = computed(() => route.params.id);

// composables
const { participants, fetchParticipants, addParticipant } =
  useParticipants(uuid);
const { participantTypes: participant_types, fetchParticipantTypes } =
  useParticipantTypes();

const mentionParticipantNames = computed(() =>
  Object.fromEntries(
    (participant_types.value || []).map((p) => [
      p.code,
      (templateLang.value === "kz" ? p.name_kz || p.name : p.name) || p.code,
    ])
  )
);

// data
const loading = ref(false);
const templateLang = ref("ru");
const visibleAddParticipant = ref(false);
const selectedRole = ref(null);
const participant_type = ref(null);
const isAccessLoading = ref(false);
const execution = ref(null);
const stepsEntityTypes = ref(null);
const activeStepCode = ref(null);
const selectedService = ref(null);
const selectedParticipant = ref(null);
const signatureList = ref([]);
const requestedServices = ref(new Map());
const placeholderDialogVisible = ref(false);
const placeholderMappingData = ref({ placeholders: [], participant: null });

// template refs
const editorRef = ref(null);
const addParticipantHeaderRef = ref(null);
const previewEditorRef = ref(null);
const participantDropdownRef = ref(null);
const formRef = ref(null);

const titleKey = computed(() => {
  return {
    kz: "content_kz",
    ru: "content",
    en: "content_en",
  }[templateLang.value];
});

const notarialView = computed(
  () =>
    route.name === "notarial_action_view" ||
    route.name === "exec_inscription_view"
);

const templateJson = computed(() => {
  const json = props.form[titleKey.value];
  return json ? JSON.parse(JSON.stringify(json)) : null;
});

const isBothLanguages = computed(() => props.form.multiple_languages);

const optionsDescription = computed(() => {
  if (!participant_type.value || !selectedRole.value) return "";
  const typeLabel =
    participant_type.value === "INDIVIDUAL" ? t("Физ лицо") : t("Юр лицо");
  return `${selectedRole.value?.name} — ${typeLabel}`;
});

const notaryData = computed(() => props.form?.notary_info);

// methods
async function fetchSignatures() {
  try {
    const { success, payload } = await getSignatureList(uuid.value);
    if (success && payload) {
      signatureList.value = payload.signatures || [];
    }
  } catch (e) {
    console.error("fetchSignatures error:", e);
  }
}

function insertNotaryData() {
  if (!notaryData.value) {
    return;
  }

  if (notaryData.value.uuid !== props.form.notary_uuid) {
    return;
  }

  const notaryFields = convertNotaryToFields(notaryData.value);

  if (notaryFields.length > 0) {
    insertFieldsAsMentions(notaryFields);
  }
}

function handleSave(json) {
  props.form[titleKey.value] = json;
  if (isBothLanguages.value) {
    props.form.content = json;
    props.form.content_kz = json;
  }
}

function toggleBothLanguages(val) {
  props.form.multiple_languages = val;
  if (val) {
    const currentJson = editorRef.value?.getDocument();
    if (currentJson) {
      props.form.content = currentJson;
      props.form.content_kz = currentJson;
    }
  }
}

function changeLang(lang) {
  templateLang.value = lang;
  insertNotaryData();
}

async function getExecution() {
  isAccessLoading.value = true;
  try {
    const response = await StepRunList(uuid.value);
    if (!response?.success) return;

    execution.value = response.payload?.execution;

    if (execution.value?.steps_entity_types) {
      stepsEntityTypes.value = execution.value?.steps_entity_types;
    }
  } finally {
    isAccessLoading.value = false;
  }
}

async function validate() {
  const forms = [formRef.value].filter(Boolean);
  const results = await Promise.all(forms.map((f) => f.validate()));
  return results.every(Boolean);
}

function handleSelect(item) {
  participant_type.value = item.type;
  selectedRole.value = item.role;
  if (uuid.value) getExecution();
  visibleAddParticipant.value = true;
}

function closeDrawer() {
  visibleAddParticipant.value = false;
  selectedRole.value = null;
  participant_type.value = null;

  if (addParticipantHeaderRef.value) {
    addParticipantHeaderRef.value.reset();
  }

  if (uuid.value) getExecution();
}

async function handleAddParticipant(participant) {
  const response = await addParticipant(participant);
  if (!response?.success) return;
  if (uuid.value) await getExecution();

  const addedParticipant = response?.payload || participant;
  selectedParticipant.value = addedParticipant;
  activeStepCode.value = null;
  startServicesQueueFor(addedParticipant);

  // Проверяем наличие default_participant_code в шаблоне
  const placeholderCode =
    props.template?.default_participant_code ||
    props.form?.default_participant_code;

  if (placeholderCode) {
    placeholderMappingData.value = {
      placeholders: [placeholderCode],
      participant: addedParticipant,
    };
    placeholderDialogVisible.value = true;
  }
}

function getParticipantLabel(participant) {
  if (!participant) return "";
  if (participant.first_name) {
    return [participant.last_name, participant.first_name]
      .filter(Boolean)
      .join(" ");
  }
  return participant.params?.company_name || participant.identifier || "";
}

function startServicesQueueFor(participant) {
  if (!participant?.uuid) return;
  if (!execution.value?.uuid) return;

  const participantType = participant.participant_type;
  const rawSteps = execution.value.steps_entity_types?.[participantType] || [];
  const steps = filterStepsForParticipant(
    rawSteps,
    participant.participant_type_code
  );
  if (!steps.length) return;

  const label = getParticipantLabel(participant);

  serviceQueue.startQueue({
    participantUuid: participant.uuid,
    participantType,
    participantTypeCode: participant.participant_type_code,
    participantLabel: label,
    executionUuid: execution.value.uuid,
    templateUuid: uuid.value,
    identifier: participant.identifier || "",
    steps,
    onStepStart: ({ stepCode, participantTypeCode }) => {
      handleServiceLoadingStart({
        service_code: stepCode,
        participant_type_code: participantTypeCode,
      });
    },
    onStepSuccess: ({ stepCode, data, participantTypeCode }) => {
      requestedServices.value.set(participant.uuid + ":" + stepCode, "success");
      const baseFields = normalizeRuntimeResponse(data, {
        locale: locale.value || "ru",
        participantTypeCode,
        serviceCode: stepCode,
      });
      const autoFields = autoSelectSingleItemFields(data, {
        locale: locale.value || "ru",
        participantTypeCode,
        serviceCode: stepCode,
      });
      const fields = [...baseFields, ...autoFields];
      if (fields.length) {
        insertFieldsAsMentions(fields);
      }
      handleServiceLoadingEnd({
        service_code: stepCode,
        participant_type_code: participantTypeCode,
        array_paths: summarizeArrayChoices(data).multi.map((m) => m.path),
      });
    },
    onStepError: ({ stepCode, stepName, error, participantTypeCode }) => {
      requestedServices.value.set(participant.uuid + ":" + stepCode, "error");
      handleServiceLoadingEnd({
        service_code: stepCode,
        participant_type_code: participantTypeCode,
      });
      toast.error(stepName || stepCode, {
        description: error?.message || t("Не удалось получить данные"),
        duration: 4000,
      });
    },
    onQueueCancel: () => {
      nextTick(() => {
        editorRef.value?.clearMentionsLoading?.();
      });
    },
    onQueueFinish: ({ summary, totalSteps }) => {
      if (totalSteps === 0) return;
      const desc = summary.error
        ? `Успешно: ${summary.success} · Ошибок: ${summary.error}`
        : `Успешно: ${summary.success} из ${totalSteps}`;
      toast.success(label ? `Готово: ${label}` : "Очередь завершена", {
        description: desc,
        duration: 3000,
      });
    },
  });
}

function confirmPlaceholderMapping(placeholder, newCode) {
  const participant = placeholderMappingData.value.participant;

  // Находим читаемое название типа участника из справочника
  const participantType = participant_types.value.find(
    (pt) => pt.code === participant?.participant_type_code
  );
  const typeName = participantType?.name || participantType?.name_kz || "";
  const fio = participant?.first_name
    ? `${participant.last_name || ""} ${participant.first_name || ""}`.trim()
    : "";
  const label = typeName ? `${typeName}${fio ? ": " + fio : ""}` : newCode;

  const updatedForm = mapRepeatSectionInAllLanguages(
    props.form,
    placeholder,
    newCode,
    label
  );

  props.form.content = updatedForm.content;
  props.form.content_kz = updatedForm.content_kz;
  props.form.content_en = updatedForm.content_en;
  props.form._repeatSectionTemplate = updatedForm._repeatSectionTemplate;

  placeholderDialogVisible.value = false;
  placeholderMappingData.value = { placeholders: [], participant: null };

  nextTick(() => {
    const currentContent = props.form[titleKey.value];
    if (editorRef.value?.editor && currentContent) {
      editorRef.value.editor.commands.setContent(currentContent);
    }
  });
}

function skipPlaceholderMapping() {
  placeholderDialogVisible.value = false;
  placeholderMappingData.value = { placeholders: [], participant: null };
}

async function handleParticipantRemoved(removedParticipant) {
  if (removedParticipant?.uuid) {
    serviceQueue.cancelQueue(removedParticipant.uuid);
  }

  await fetchParticipants();

  if (!removedParticipant?.participant_type_code) return;

  // Удаляем секцию этого участника
  let updatedForm = removeRepeatSectionByCodeInAllLanguages(
    props.form,
    removedParticipant.participant_type_code
  );

  // Если не осталось repeatSection — восстанавливаем оригинал с placeholder
  const currentDoc = updatedForm[titleKey.value];
  const hasAnySections = currentDoc?.content?.some(
    (n) => n.type === "repeatSection"
  );

  if (!hasAnySections && updatedForm._repeatSectionTemplate) {
    updatedForm = restoreOriginalRepeatSection(updatedForm);
  }

  props.form.content = updatedForm.content;
  props.form.content_kz = updatedForm.content_kz;
  props.form.content_en = updatedForm.content_en;

  nextTick(() => {
    const currentContent = props.form[titleKey.value];
    if (editorRef.value?.editor && currentContent) {
      editorRef.value.editor.commands.setContent(currentContent);
    }
  });
}

function onSelectStep(step) {
  activeStepCode.value = step?.code || null;

  if (activeStepCode.value === "internal_databases") {
    insertNotaryData();
  }
}

function insertFieldAsMention(field) {
  if (!field) return;
  insertFieldsAsMentions([field]);
}

function handleServiceLoadingStart({
  service_code,
  participant_type_code,
} = {}) {
  if (!service_code) return;
  nextTick(() => {
    editorRef.value?.setMentionsLoading?.({
      serviceCode: service_code,
      participantTypeCode: participant_type_code,
    });
  });
}

function handleServiceLoadingEnd({
  service_code,
  participant_type_code,
  array_paths,
} = {}) {
  if (service_code) {
    const scope = {
      serviceCode: service_code,
      participantTypeCode: participant_type_code,
      excludeKeyPrefixes: array_paths,
    };
    const updatedForm = fillEmptyMentionsWithDashInAllLanguages(
      props.form,
      scope
    );
    ["content", "content_kz", "content_en"].forEach((k) => {
      props.form[k] = updatedForm[k];
    });
  }
  nextTick(() => {
    editorRef.value?.clearMentionsLoading?.({
      serviceCode: service_code,
      participantTypeCode: participant_type_code,
    });
    if (service_code) {
      editorRef.value?.fillEmptyMentionsWithDash?.({
        serviceCode: service_code,
        participantTypeCode: participant_type_code,
        excludeKeyPrefixes: array_paths,
      });
    }
  });
}

function handleDashArraySelected({
  service_code,
  participant_type_code,
  array_path,
} = {}) {
  if (!service_code || !array_path) return;
  const scope = {
    serviceCode: service_code,
    participantTypeCode: participant_type_code,
    includeKeyPrefixes: [array_path],
  };
  const updatedForm = fillEmptyMentionsWithDashInAllLanguages(
    props.form,
    scope
  );
  ["content", "content_kz", "content_en"].forEach((k) => {
    props.form[k] = updatedForm[k];
  });
  nextTick(() => {
    editorRef.value?.fillEmptyMentionsWithDash?.(scope);
  });
}

function insertFieldsAsMentions(fields) {
  if (!Array.isArray(fields) || fields.length === 0) {
    console.warn("insertFieldsAsMentions: no fields provided");
    return;
  }

  // Обновляем форму для всех языков используя новый сервис
  const updatedForm = updateMentionsInAllLanguages(props.form, fields);

  // Обновляем текущий документ
  Object.keys(updatedForm).forEach((key) => {
    if (key === "content" || key === "content_kz" || key === "content_en") {
      props.form[key] = updatedForm[key];
    }
  });

  // Также обновляем текущий редактор для немедленного отображения
  nextTick(() => {
    if (editorRef.value) {
      editorRef.value.insertServiceFields(fields);
    }
  });
}

async function onInsertSavedFields(participant) {
  if (!participant) return;
  const serviceFields = participant.service_fields;
  if (!serviceFields || typeof serviceFields !== "object") return;

  const entries = Object.entries(serviceFields).filter(
    ([, payload]) =>
      payload && typeof payload === "object" && Object.keys(payload).length > 0
  );
  if (!entries.length) return;

  const collected = [];
  for (const [serviceCode, payload] of entries) {
    const options = {
      locale: locale.value || "ru",
      participantTypeCode: participant.participant_type_code,
      serviceCode,
    };
    const baseFields = normalizeRuntimeResponse(payload, options);
    const autoFields = autoSelectSingleItemFields(payload, options);
    collected.push(...baseFields, ...autoFields);
  }

  if (!collected.length) {
    toast.info(t("Нет данных для вставки"), { duration: 2000 });
    return;
  }

  const participantFio =
    [participant.first_name, participant.last_name, participant.middle_name]
      .filter(Boolean)
      .join(" ") || t("Участник");

  try {
    await confirm(
      t("У {fio} сохранено данных: {count}. Подставить их в текст документа?", {
        fio: participantFio,
        count: collected.length,
      }),
      t("Подтверждение"),
      {
        confirmButtonText: t("Вставить"),
        cancelButtonText: t("Отмена"),
      }
    );
  } catch {
    return;
  }

  insertFieldsAsMentions(collected);
  toast.success(t("Данные ГБД"), {
    description: t("{count} значений вставлено", { count: collected.length }),
    duration: 2500,
  });
}

async function changeMode() {
  await previewEditorRef.value.open({});
}

function openMentionDropdown({
  participant_type_code,
  onSave,
  onDelete,
  onRestore,
  onNumberToWords,
  hasValue,
  rect,
}) {
  const selectedCodes = [
    ...new Set(participants.value.map((p) => p.participant_type_code)),
  ];
  participantDropdownRef.value.openDropdown({
    selectedType: participant_type_code,
    selectedCodes,
    onSave,
    onDelete,
    onRestore,
    onNumberToWords,
    hasValue,
    rect,
  });
}

function insertNotarialNumber() {
  if (!uuid.value && !props.form.number) return;
  const notarial_number_data = {
    value: props.form.number,
    key: "number",
    label: t("№ нотариального действия"),
    isCustom: true,
  };

  insertFieldAsMention(notarial_number_data);
}

function hasEmptyMentionByKey(node, key) {
  if (!node || typeof node !== "object") return false;
  if (node.type === "mention" && node.attrs?.key === key) {
    const value = node.attrs.value;
    if (!value || String(value).trim() === "") return true;
  }
  if (Array.isArray(node.content)) {
    return node.content.some((child) => hasEmptyMentionByKey(child, key));
  }
  return false;
}

function insertCurrentDay() {
  if (props.form.status !== StatusCode.PROJECT) return;

  const hasEmpty = ["content", "content_kz", "content_en"].some((k) =>
    hasEmptyMentionByKey(props.form[k], "day")
  );
  if (!hasEmpty) return;

  const now = moment();
  const dateObj = {
    year: now.year(),
    month: now.month() + 1,
    day: now.date(),
  };
  const localizedValues = {
    ru: dateToWords(dateObj, "ru"),
    kz: dateToWords(dateObj, "kz"),
    en: dateToWords(dateObj, "en"),
  };

  const current_day_data = {
    value: localizedValues.ru,
    key: "day",
    label: t("День"),
    isCustom: true,
    localizedValues,
    dateISO: now.format("YYYY-MM-DD"),
  };
  insertFieldAsMention(current_day_data);
}

// created
insertNotarialNumber();
insertCurrentDay();

// mounted
onMounted(() => {
  insertNotaryData();
  fetchParticipantTypes();

  if (uuid.value) {
    fetchParticipants();
    getExecution();
    fetchSignatures();
  }
});

defineExpose({ validate, insertFieldsAsMentions });
</script>

<template>
  <div class="template">
    <div class="block">
      <TipTapEditor2
        :mode="notarialView ? 'view' : 'edit'"
        :allow-unwrap-repeat-section="false"
        :with-border="true"
        :show-preview-button="true"
        :templateLang="templateLang"
        :json="templateJson"
        :signatures="signatureList"
        :serviceFields="selectedService"
        :isBothLanguages="isBothLanguages"
        :templateItems="templateItems"
        :mention-participant-names="mentionParticipantNames"
        @update:json="handleSave"
        @update:isBothLanguages="toggleBothLanguages"
        @change:lang="changeLang"
        @change:mode="changeMode"
        @edit:mention="openMentionDropdown"
        @select:template="$emit('select:template', $event)"
        ref="editorRef"
      />
    </div>

    <div class="block h-full">
      <template v-if="notarialView">
        <NotarialActionDataView
          :loading="loading"
          :form="form"
          :participants="participants"
        />
      </template>
      <template v-else>
        <div class="inner h-full flex">
          <div v-if="participants?.length" class="flex w-full h-full">
            <div
              class="border border-0 border-r border-solid border-border w-1/2 h-full"
            >
              <div class="p-6 h-full overflow-y-auto">
                <ServiceExecutionBlock
                  :uuid="uuid"
                  :form="form"
                  :execution="execution"
                  :notaries-info="notaryData"
                  :activeStepCode="activeStepCode"
                  :template="template"
                  :selected-participant="selectedParticipant"
                  :participant_type="selectedParticipant?.participant_type"
                  @service-response-changed="
                    (val) => {
                      selectedService = val;
                      if (selectedParticipant?.uuid && activeStepCode) {
                        requestedServices.set(
                          selectedParticipant.uuid + ':' + activeStepCode,
                          'success'
                        );
                      }
                    }
                  "
                  @service-response-error="
                    () => {
                      if (selectedParticipant?.uuid && activeStepCode) {
                        requestedServices.set(
                          selectedParticipant.uuid + ':' + activeStepCode,
                          'error'
                        );
                      }
                    }
                  "
                  @insert:field="insertFieldAsMention"
                  @insert:fields="insertFieldsAsMentions"
                  @service-loading-start="handleServiceLoadingStart"
                  @service-loading-end="handleServiceLoadingEnd"
                  @dash:array-selected="handleDashArraySelected"
                />
              </div>
            </div>

            <div class="w-1/2 h-full">
              <div class="flex flex-col gap-6 p-6 h-full overflow-y-auto">
                <AddParticipantHeader
                  ref="addParticipantHeaderRef"
                  :template="template"
                  :participant_type="participant_type"
                  :participants="participants"
                  :participant-types="participant_types"
                  @select="handleSelect"
                />

                <ParticipantList
                  v-if="participants?.length"
                  :participant_type="participant_type"
                  :participants="participants"
                  :participant-types="participant_types"
                  :stepsEntityTypes="stepsEntityTypes"
                  :activeService="activeStepCode"
                  :template="template"
                  :selected-participant="selectedParticipant"
                  :isAccessLoading="isAccessLoading"
                  :loading="loading"
                  :requestedServices="requestedServices"
                  @update-data="handleParticipantRemoved"
                  @select:step="onSelectStep"
                  @select:participant="(val) => (selectedParticipant = val)"
                  @insert:saved-fields="onInsertSavedFields"
                />
              </div>
            </div>
          </div>

          <div v-else class="w-full h-full">
            <div class="flex flex-col gap-6 p-6 h-full">
              <AddParticipantHeader
                ref="addParticipantHeaderRef"
                :template="template"
                :participant_type="participant_type"
                :participant-types="participant_types"
                :participants="participants"
                @select="handleSelect"
              />

              <div
                class="w-full p-4 bg-card flex flex-col items-start justify-center gap-4"
                style="height: calc(100vh - 330px)"
              >
                <div class="w-full flex flex-col items-center gap-2.5">
                  <svg-icon
                    name="huge-user-plus"
                    class="w-8 h-8 text-muted-foreground"
                  />
                  <div
                    class="text-muted-foreground text-sm font-medium leading-5"
                  >
                    {{ t("Добавьте участника") }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <AddParticipants
          v-if="visibleAddParticipant"
          :description="optionsDescription"
          :form="form"
          :participant_type="participant_type"
          :participant-types="participant_types"
          :selectedRole="selectedRole"
          :execution="execution"
          @close="closeDrawer"
          @save="handleAddParticipant"
        />
      </template>
    </div>

    <PreviewTipTap2
      :templateLang="templateLang"
      :templateJson="templateJson"
      :content-by-lang="{
        ru: form.content,
        kz: form.content_kz,
        en: form.content_en,
      }"
      :signatures="signatureList"
      ref="previewEditorRef"
    />

    <ParticipantDropdown ref="participantDropdownRef" />

    <PlaceholderMappingDialog
      v-model:visible="placeholderDialogVisible"
      :placeholders="placeholderMappingData.placeholders"
      :participant="placeholderMappingData.participant"
      @confirm="confirmPlaceholderMapping"
      @skip="skipPlaceholderMapping"
    />
  </div>
</template>

<style scoped lang="scss">
.template {
  @apply flex h-full w-full overflow-x-hidden gap-5;

  .block {
    @apply flex flex-col w-1/2 h-full  rounded-2xl gap-6 overflow-hidden;
    background-color: hsl(var(--background));

    min-height: 600px !important;

    box-shadow: 0 1px 1px -0.5px rgba(0, 0, 0, 0.03),
      0 3px 3px -1.5px rgba(0, 0, 0, 0.03),
      0 20px 20px -12px rgba(0, 0, 0, 0.03);

    > .inner {
      @apply flex-1 overflow-auto;
    }
  }
}

@media (max-width: 1400px) {
  .template {
    @apply flex flex-col;

    .block {
      @apply w-full;
    }
  }
}
</style>
